import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import classNames from 'classnames'
import { Checkbox } from 'uiComponents/input'
import Money from 'uiComponents/money'
import {
    getSelectedOrderItemsByTableIdAndRowId,
    shouldToggleOrder,
    toggleOrder,
    toggleOrderItem,
} from 'orders/orders/redux'
import ExpandIcon from '../../commons/expandIcon'
import { IOrder, IOrderItem } from 'orders/schema'
import ItemOptionsSection from './itemOptionsSection'
import ItemDetailsSection from './itemDetailsSection'
import ItemAPFSection from './itemAPFSection'
import './styles.scss'
import ItemCancelationRefundSection from './itemCancelationRefundSection'
import Infotip from 'uiComponents/infotip'
import { useHasFeature } from 'utils/useHasFeature'
import { UnstyledLink } from 'uiComponents/navigation/unstyledLink'
import { useTranslation } from 'translations'

interface OrderItemProps {
    item: IOrderItem
    accountSlug: string
    order: IOrder
    shouldBeExpanded: boolean
    shouldShowPricesWithoutTax: boolean
}

const OrderItem = ({ item, accountSlug, order, shouldBeExpanded, shouldShowPricesWithoutTax }: OrderItemProps) => {
    const { t } = useTranslation()
    const orderUuid = order.id
    const [isExpanded, setIsExpanded] = React.useState(shouldBeExpanded ?? true)
    const dispatch = useDispatch()
    const orderShouldToggle = useSelector(shouldToggleOrder(orderUuid, item.id))
    const isItemSelected = useSelector(getSelectedOrderItemsByTableIdAndRowId(orderUuid, item.id))
    const isItemCanceledRefunded = !!item.refundInfo
    const bookingFeesEnabled = useHasFeature({ featureName: 'BookingFees' })

    useEffect(() => {
        if (shouldBeExpanded !== isExpanded) {
            setIsExpanded(shouldBeExpanded)
        }
    }, [shouldBeExpanded])

    const handleCheckboxChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            dispatch(toggleOrderItem({ rowId: item.id, tableId: orderUuid }))
            if (orderShouldToggle) {
                dispatch(toggleOrder({ orderUuid, selected: e.target.checked }))
            }
        },
        [dispatch, item.id, orderShouldToggle, orderUuid],
    )

    return (
        <div className="item">
            <div
                className={classNames('item-row', {
                    selected: isItemSelected,
                    canceledRefunded: isItemCanceledRefunded,
                })}
            >
                <ExpandIcon isExpanded={isExpanded} setIsExpanded={setIsExpanded} />
                <Checkbox
                    id={`order-item-${item.id}-checkbox`}
                    checked={isItemCanceledRefunded ? false : isItemSelected}
                    onChange={handleCheckboxChange}
                    disabled={isItemCanceledRefunded}
                />
                <label htmlFor={`order-item-${item.id}-checkbox`}>
                    <strong>{item.product + ` (${item.productNumericId})`}</strong>
                </label>
                {item.membership && (
                    <UnstyledLink
                        className="view-membership"
                        to={`#membership_details?membershipUuid=${item.membership}`}
                    >
                        {t('orders.orders.orderDetails.items.viewMembership')}
                    </UnstyledLink>
                )}
                <div
                    className="expand"
                    onClick={() => {
                        setIsExpanded(!isExpanded)
                    }}
                />
                {shouldShowPricesWithoutTax && (
                    <span className="price-excl-tax">
                        {t('orders.orders.orderDetails.items.priceExclTax')}&nbsp;
                        <strong>
                            <Money amount={item.discountedPriceExclTax} accountSlug={accountSlug} />
                        </strong>
                    </span>
                )}
                <span className="price-incl-tax">
                    {t('orders.orders.orderDetails.items.price')}&nbsp;
                    <strong>
                        <Money amount={item.discountedPriceInclTax} accountSlug={accountSlug} />
                    </strong>
                    <Infotip>
                        {bookingFeesEnabled
                            ? t('orders.orders.orderDetails.items.includesFeesAndDiscounts')
                            : t('orders.orders.orderDetails.items.includesDiscounts')}
                    </Infotip>
                </span>
            </div>
            {isExpanded && (
                <div>
                    <ItemDetailsSection
                        item={item}
                        order={order}
                        isItemSelected={isItemSelected}
                        isItemCanceledRefunded={isItemCanceledRefunded}
                        accountSlug={accountSlug}
                        shouldShowPricesWithoutTax={shouldShowPricesWithoutTax}
                        shouldShowBookingFees={bookingFeesEnabled}
                    />
                    {item.options.length > 0 && (
                        <ItemOptionsSection
                            options={item.options}
                            isItemSelected={isItemSelected}
                            isItemCanceledRefunded={isItemCanceledRefunded}
                            accountSlug={accountSlug}
                            shouldShowPricesWithoutTax={shouldShowPricesWithoutTax}
                        />
                    )}
                    {!isEmpty(item.afterPaymentForm) && (
                        <ItemAPFSection
                            item={item}
                            isItemSelected={isItemSelected}
                            isItemCanceledRefunded={isItemCanceledRefunded}
                            accountSlug={accountSlug}
                        />
                    )}
                    {!!item.refundInfo && (
                        <ItemCancelationRefundSection
                            item={item}
                            accountSlug={accountSlug}
                            isItemSelected={isItemSelected}
                        />
                    )}
                </div>
            )}
        </div>
    )
}

export default OrderItem
